import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Typography, Button, Box, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import CircularProgress from "@mui/material/CircularProgress";
import cookie from "react-cookies";
import { subdomain, getBackendUrl } from '../../auth/utilities';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import SnackbarAlert from '../../components/SnackbarAlert/SnackbarAlert';
import theme from '../../theme';
import "./UserTrainingList.css";

export default function UserTrainingList() {
    const navigate = useNavigate();
    const hasFetchedRef = useRef(false);
    const location = useLocation();
    const backend = getBackendUrl();

    const [trainings, setTrainings] = useState([]);
    const [loadingTrainingId, setLoadingTrainingId] = useState(null);
    const [certificateUrl, setCertificateUrl] = useState(null);
    const [viewLoadingId, setViewLoadingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingDownload, setLoadingDownload] = useState(null);
    const [currentCertificateId, setCurrentCertificateId] = useState(null);

    const csrfToken = cookie.load("csrftoken");
    const { id } = useParams();

    const normalizeTrainingStatus = (status) =>
        (status || "")
            .trim()
            .toLowerCase()
            .replace(/-/g, " ")
            .replace(/\s+/g, " ");

    // Load data
    useEffect(() => {
        if (location.state?.trainings) {
            setTrainings(location.state.trainings);
            return;
        }
        const savedData = sessionStorage.getItem("trainingList");
        if (savedData) {
            setTrainings(JSON.parse(savedData));
        }
    }, [location.state]);

    // Prevent back button
    useEffect(() => {
        const blockBack = () =>
            navigate("/whizz-learning/training-list/", { replace: true });
        window.addEventListener("popstate", blockBack);
        return () => window.removeEventListener("popstate", blockBack);
    }, [navigate]);

    // View certificate
    const viewCertificate = async (trainingId) => {
        try {
            setViewLoadingId(trainingId);
            const res = await fetch(
                `${backend}${subdomain}/${cookie.load("Language") || "en"}/trainingmodule/getcertificate/${trainingId}/`,
                { method: "GET", credentials: "include", headers: { "Content-Type": "application/json" } }
            );
            const data = await res.json();
            if (data.status === "success" && data.certificate_url) {
                setCertificateUrl(data.certificate_url);
                setCurrentCertificateId(trainingId);
            } else if (data.error === "Training login required") {
                navigate("/whizz-learning/");
            } else if (data.status === "error" || data.error) {
                alert(data.message || data.error || "Failed to load certificate.");
            }
        } catch (error) {
            alert("Error loading certificate");
        } finally {
            setViewLoadingId(null);
        }
    };

    // Status colors
    const getStatusStyle = (status) => {
        switch (normalizeTrainingStatus(status)) {
            case "completed":
                return { bg: "#E7FCEB", color: "#1E8E3E" };
            case "in progress":
            case "overdue":
            case "expired":
                return { bg: "#FFF4CC", color: "#B58100" };
            case "initiated":
                return { bg: "#EAF1FF", color: "#1F4DB8" };
            case "not initiated":
            default:
                return { bg: "#f89494ff", color: "#850101ff" };
        }
    };

    // Update training
    const updateTrainingStatus = async (trainingId, currentStatus) => {
        const normalizedStatus = normalizeTrainingStatus(currentStatus);
        const alreadyStartedStatuses = [
            "in progress",
            "overdue"
        ];

        if (alreadyStartedStatuses.includes(normalizedStatus)) {
            navigate(`/whizz-learning/training-video/${trainingId}`, {
                state: { training_status: currentStatus },
            });
            return;
        }

        try {
            setLoadingTrainingId(trainingId);
            const response = await fetch(
                `${backend}${subdomain}/${cookie.load("Language") || "en"}/trainingmodule/updatetraining/${trainingId}/`,
                {
                    method: "PUT",
                    credentials: "include",
                    headers: { "Content-Type": "application/json", "X-CSRFToken": cookie.load("csrftoken") },
                    body: JSON.stringify({ training_status: "In Progress", training_id: trainingId }),
                }
            );
            const data = await response.json();
            if (data?.error === "Training login required") {
                navigate("/whizz-learning/");
                return;
            }
            if (data.status === "success") {
                navigate(`/whizz-learning/training-video/${trainingId}`, {
                    state: { training_status: "In Progress" },
                });
            } else {
                alert(data.message || "Failed to update training status.");
            }
        } catch (error) {
            alert("Unable to reach server.");
        } finally {
            setLoadingTrainingId(null);
        }
    };

    const viewTrainingDirectly = (trainingId, trainingStatus) => {
        navigate(`/whizz-learning/training-video/${trainingId}`, {
            state: { training_status: trainingStatus },
        });
    };

    // Logout
    const handleLogout = async () => {
        try {
            setLoading(true);
            const response = await fetch(
                `${backend}${subdomain}/en/trainingmodule/logouttraining/`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
                }
            );
            const data = await response.json();
            if (!response.ok) { console.log("Logout error:", data?.error || "Logout failed"); return; }
            if (data?.message) {
                localStorage.removeItem("training_token");
                navigate("/whizz-learning/");
            }
        } catch (error) {
            navigate("/whizz-learning/");
        } finally {
            setLoading(false);
        }
    };

    const downloadCertificate = async (trainingId) => {
        if (!trainingId) return;
        setLoadingDownload(trainingId);
        try {
            const language = cookie.load("Language") || "en";
            const apiUrl = `${backend}${subdomain}/${language}/trainingmodule/getcertificate/${trainingId}/`;
            const res = await fetch(apiUrl, { credentials: "include" });
            if (!res.ok) throw new Error("Failed to fetch certificate URL");
            const data = await res.json();
            const fileUrl = data.certificate_url;
            window.location.href = fileUrl;
        } catch (error) {
            console.error("Download failed:", error);
        } finally {
            setLoadingDownload(null);
        }
    };

    const reloadTrainingList = async () => {
        try {
            const response = await fetch(
                `${backend}${subdomain}/en/trainingmodule/traininglist/`,
                { method: "GET", credentials: "include", headers: { "Content-Type": "application/json" } }
            );
            const data = await response.json();
            if (response.ok && data.status !== "error") {
                setTrainings(data.trainings || []);
                sessionStorage.setItem("trainingList", JSON.stringify(data.trainings || []));
            } else if (data.status === "error" || data.error) {
                alert(data.message || data.error || "Failed to load training list.");
            }
        } catch (err) {
            console.log("Reload Error:", err);
            alert("An error occurred while loading the training list.");
        }
    };

    useEffect(() => { reloadTrainingList(); }, []);

    return (
        <div className="whizz-learning-training-list-root">
            {/* NAVBAR */}
            <Box className="training-navbar">
                <div
                    className={`navbar-back-title ${certificateUrl ? "clickable" : ""}`}
                    onClick={() => { if (certificateUrl) { setCertificateUrl(null); } }}
                >
                    {certificateUrl && <ArrowBackIcon className="navbar-back-icon" />}
                    <h3 className="navbar-title">
                        {certificateUrl ? "Certificate" : "Training List"}
                    </h3>
                </div>
                <Button
                    className="navbar-logout-btn"
                    onClick={handleLogout}
                    disabled={loading}
                    style={{ opacity: loading ? 0.7 : 1 }}
                >
                    {loading ? "Logging out..." : "Logout"}
                </Button>
            </Box>

            {/* CERTIFICATE VIEW */}
            {certificateUrl ? (
                <div className="certificate-view-container">
                    {/* Decorative particles */}
                    {[["#ffde59", "20%", "10%", "1.6s"], ["#ff57e0", "40%", "15%", "2s,0.3s"], ["#4dff8c", "60%", "8%", "1.8s,0.6s"], ["#5dbaff", "75%", "12%", "2.2s,0.9s"]].map(([bg, top, left, anim], i) => (
                        <div key={i} className="firework-particle" style={{ background: bg, animation: `firework ${anim} ease-out infinite`, top, left }} />
                    ))}
                    {currentCertificateId && (
                        <Button
                            key={currentCertificateId}
                            onClick={() => downloadCertificate(currentCertificateId)}
                            variant="contained"
                            disabled={loadingDownload === currentCertificateId}
                            startIcon={loadingDownload === currentCertificateId ? <CircularProgress size={20} color="inherit" /> : <DownloadForOfflineIcon style={{ fontSize: "23px" }} />}
                            className="download-cert-btn"
                            style={{ opacity: loadingDownload === currentCertificateId ? 0.8 : 1 }}
                        >
                            Download Certificate
                        </Button>
                    )}
                    <img src={certificateUrl} alt="Certificate" onContextMenu={(e) => e.preventDefault()} className="certificate-image" />
                </div>
            ) : (
                // TRAINING LIST VIEW
                <div className="training-list-wrapper">
                    <div className="training-list-card">
                        {trainings.length === 0 ? (
                            <div className="no-trainings-container">
                                <div className="no-trainings-icon-box">
                                    <span className="no-trainings-icon">📚</span>
                                </div>
                                <h2 className="no-trainings-heading">No Trainings Available Yet</h2>
                                <p className="no-trainings-text">
                                    There are currently no training programs assigned to you. Check back later or contact your administrator for more information.
                                </p>
                            </div>
                        ) : (
                            trainings.map((t) => {
                                const normalizedStatus = normalizeTrainingStatus(t.training_status);
                                const { bg, color } = getStatusStyle(t.training_status);
                                return (
                                    <div key={t.training_id} className="training-item">
                                        <img src={t.training_cover_image} alt="thumb" className="training-thumbnail" />
                                        <div className="training-info">
                                            <div className="training-header">
                                                <h3 className="training-title">{t.training_name}</h3>
                                                <span className="training-expiry-msg" style={{ color: t.expiry_message === "Training Completed" ? "#00c52e" : "#f73527" }}>
                                                    {t.expiry_message}
                                                </span>
                                            </div>
                                            <p className="training-description">{t.training_description}</p>

                                            {["not initiated", "initiated", "in progress", "overdue"].includes(normalizedStatus) && (
                                                <Button
                                                    className="start-training-btn"
                                                    disabled={loadingTrainingId === t.training_id}
                                                    onClick={() => updateTrainingStatus(t.training_id, t.training_status)}
                                                >
                                                    {loadingTrainingId === t.training_id ? "Loading..." : ["not initiated", "initiated"].includes(normalizedStatus) ? "Start Training" : "Continue Training"}
                                                </Button>
                                            )}

                                            {normalizedStatus === "completed" && (
                                                <Box display="flex" gap="10px" marginTop="10px">
                                                    <Button
                                                        className="view-cert-btn"
                                                        disabled={viewLoadingId === t.training_id}
                                                        onClick={() => viewCertificate(t.training_id)}
                                                        style={{ opacity: viewLoadingId === t.training_id ? 0.7 : 1 }}
                                                    >
                                                        {viewLoadingId === t.training_id ? <CircularProgress size={16} color="inherit" /> : "View Certificate"}
                                                    </Button>
                                                    <Button
                                                        className="view-training-btn"
                                                        disabled={loadingTrainingId === t.training_id}
                                                        onClick={() => viewTrainingDirectly(t.training_id, t.training_status)}
                                                        style={{ opacity: loadingTrainingId === t.training_id ? 0.7 : 1 }}
                                                    >
                                                        {loadingTrainingId === t.training_id ? <CircularProgress size={16} color="inherit" /> : "View Training"}
                                                    </Button>
                                                </Box>
                                            )}
                                        </div>
                                        <div className="status-badge" style={{ background: bg, color: color }}>
                                            {t.training_status}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
